import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import type { To } from 'react-router-dom';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { isType } from '@sorare/core/src/lib/gql';

import { isGameScheduled } from '@sorare/marketplace/src/shared/game/lib';

import { CompetitionName } from './CompetitionName';
import { GameTeamsMatchup } from './GameTeamsMatchup';
import { GameWrapper } from './GameWrapper';
import type { Game_anyGameInterface } from './__generated__/index.graphql';

const SpaceBetweenHorizontal = styled(SBHorizontal)`
  padding: var(--double-unit) var(--double-unit) var(--intermediate-unit);
  min-height: var(--triple-unit);
  min-width: 0;
`;
const StyledButtonBase = styled(LinkOverlay).attrs({ as: ButtonBase })`
  width: 100%;
`;

type Props = {
  game: Game_anyGameInterface;
  topRight?: ReactNode;
  footer?: ReactNode;
  selected?: boolean;
  to?: To;
  onClick?: () => void;
} & Pick<React.ComponentProps<typeof GameTeamsMatchup>, 'displayDay'> &
  Pick<
    React.ComponentProps<typeof GameWrapper>,
    'labelProps' | 'withLightning' | 'transparent'
  >;

export const Game = ({
  game,
  topRight,
  footer,
  labelProps,
  withLightning,
  transparent,
  selected,
  to,
  onClick,
  displayDay,
}: Props) => {
  const matchup = <GameTeamsMatchup game={game} displayDay={displayDay} />;

  const wrapper = (
    <GameWrapper
      className={classNames({ selected })}
      transparent={transparent}
      withLightning={withLightning}
      labelProps={labelProps}
    >
      <SpaceBetweenHorizontal gap={1}>
        <CompetitionName displayName={game.competition.displayName} />
        {topRight}
      </SpaceBetweenHorizontal>
      {to || onClick ? (
        <ConditionalWrapper
          wrap={!isType(game, 'Game') || !isGameScheduled(game.status)}
          Wrapper={StyledButtonBase}
          props={{ onClick, to }}
        >
          {matchup}
        </ConditionalWrapper>
      ) : (
        matchup
      )}
      {footer}
    </GameWrapper>
  );

  if (to) {
    return <LinkBox>{wrapper}</LinkBox>;
  }

  return wrapper;
};

Game.fragments = {
  anyGameInterface: gql`
    fragment Game_anyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      competition {
        slug
        displayName
      }
      ...GameTeamsMatchup_anyGameInterface
    }
    ${GameTeamsMatchup.fragments.anyGameInterface}
  ` as TypedDocumentNode<Game_anyGameInterface>,
};
